import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { HttpService } from '../../../services/http.service';
import { debounceTime, distinctUntilChanged, pairwise, Subject, switchMap, takeUntil } from 'rxjs';
import differenceInYears from 'date-fns/differenceInYears';
import { ToastrService } from 'ngx-toastr';
import { StorageService } from '../../../services/storage.service';
import { UserAccountDTO } from '../../../classes/user-account.dto';
import { DataService } from '../../../services/data.service';

import { FiscalCodeService } from '../../../services/fiscal-code.service';
import { AriaAutocompleteFieldComponent, AriaFieldOption } from '../../shared';
import { AriaDatePickerFieldComponent } from '../../shared/aria-date-picker-field/aria-date-picker-field.component';

@Component({
  selector: 'app-modal-create-profile',
  imports: [TranslateModule, ReactiveFormsModule, NzGridModule, NzCheckboxModule, AriaAutocompleteFieldComponent, AriaDatePickerFieldComponent],
  templateUrl: './modal-create-profile.component.html',
  styleUrls: ['./modal-create-profile.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ModalCreateProfileComponent implements OnInit, OnDestroy {
  createProfileForm?: FormGroup;
  isAdult: boolean = true;
  startBirthdate = new Date(new Date().getFullYear() - 120, new Date().getMonth(), new Date().getDate());
  endBirthdate = new Date();
  companyId?: string;
  readonly nzModalData = inject(NZ_MODAL_DATA);
  dataModal: any;
  @Output() closeModal: EventEmitter<any> = new EventEmitter<any>();
  destroy$ = new Subject<void>();
  districts: AriaFieldOption[] = [];
  cities: AriaFieldOption[] = [];   
  genderOptions: AriaFieldOption[] = [
    {
      value: 'M',
      label: 'M',
    },
    {
      value: 'F',
      label: 'F',
    },
  ];

  constructor(
    private modalRef: NzModalRef,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private http: HttpService,
    private toast: ToastrService,
    private storage: StorageService,
    private translate: TranslateService,
    private fiscalCodeService: FiscalCodeService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.createForm();
    this.dataModal = this.nzModalData.dataModal;
  }

  private createForm(): void {
    this.createProfileForm = this.fb.group({
      name: [null, [Validators.required]],
      surname: [null, [Validators.required]],
      birthdate: [null, [Validators.required]],
      calculateTaxcode: [false],
      taxcode: [null, [Validators.required]],
      email: [null, [Validators.email, Validators.required]],
      phone: [null, [Validators.required, Validators.pattern(/^(?:\+?[0-9]{8,})$/)]],
    });
    if (this.createProfileForm) {
      this.toggleFormTaxcodeSection();
      this.checkAge();
    }
  }

  private parseBirthDate(value: string | Date): Date {
    if (value instanceof Date) {
      return new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate()
      );
    }
    const [year, month, day] = value
      .substring(0, 10)
      .split('-')
      .map(Number);
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid birthdate');
    }
    return date;
  }

  private checkAge(): void {
    this.createProfileForm!.get('birthdate')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((birthdate) => {
        if (birthdate) {
          const today = new Date();
          const age = differenceInYears(today, new Date(birthdate));
          this.isAdult = age >= 18;
          if (this.isAdult) {
            this.createProfileForm?.removeControl('legalProtectionMinor');
            this.createProfileForm?.get('legalProtectionMinor')?.updateValueAndValidity();
            this.createProfileForm?.addControl('phone', new FormControl(null, [Validators.required, Validators.pattern(/^(?:\+?[0-9]{8,})$/)]));
            this.createProfileForm?.get('phone')?.updateValueAndValidity();
            this.createProfileForm?.addControl('email', new FormControl(null, [Validators.email, Validators.required]));
            this.createProfileForm?.get('email')?.updateValueAndValidity();
          } else {
            this.createProfileForm?.addControl('legalProtectionMinor', new FormControl(false, Validators.requiredTrue));
            this.createProfileForm?.get('legalProtectionMinor')?.updateValueAndValidity();
            this.createProfileForm?.removeControl('phone');
            this.createProfileForm?.get('phone')?.updateValueAndValidity();
            this.createProfileForm?.removeControl('email');
            this.createProfileForm?.get('email')?.updateValueAndValidity();
          }
        }
      });
    this.cdr.detectChanges();
  }

  private toggleFormTaxcodeSection(): void {
    this.createProfileForm!.get('calculateTaxcode')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        if (value) {
          this.getBirthDistrict();
          this.createProfileForm?.addControl(
            'taxcodeSection',
            this.fb.group({
              gender: ['', Validators.required],
              birthDistrict: ['', Validators.required],
              birthCity: ['', Validators.required],
            })
          );
          this.listenerOnBirthDistrict();
          this.listenerOnForm();
        } else {
          this.createProfileForm?.removeControl('taxcodeSection');
        }
        this.cdr.detectChanges();
      });
  }

  private getBirthDistrict(): void {
    this.http
      .getDistrict('IT')
      .pipe(takeUntil(this.destroy$))
      .subscribe((listDistrict: Array<{ description: string; code: string }>) => {
        this.districts = listDistrict.map((item: { description: string; code: string }) => ({
          value: item.code,
          label: item.description,
        }));
      });
  }

  private listenerOnBirthDistrict(): void {
    this.createProfileForm!.get('taxcodeSection.birthDistrict')!
      .valueChanges.pipe(
        switchMap((districtCode: string) => {
        this.createProfileForm?.get('taxcodeSection.birthCity')?.patchValue('');
        this.createProfileForm?.get('taxcodeSection.birthCity')?.updateValueAndValidity();
        this.cities = [];
        return this.http.getProvince(districtCode);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((listProvince: Array<{ description: string; code: string }>) => {
        this.cities = listProvince.map((item: { description: string; code: string }) => ({
          value: item.code,
          label: item.description,
        }));
        this.cdr.detectChanges();
      });
  }

  private listenerOnForm(): void {
    this.createProfileForm!.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), pairwise(), takeUntil(this.destroy$)).subscribe(([previous, current]) => {
      if (previous.taxcode !== current.taxcode) {
        return;
      }
      this.clearControlValidators('taxcode');
      !this.isAdult ? this.clearControlValidators('legalProtectionMinor') : null;
      if (this.createProfileForm?.get('taxcodeSection') && this.getFormValidation() && this.allTaxCodeFieldsAreFilled()) {
        if (current.taxcode) {
          if (this.checkOldValues(previous, current)) {
            this.calculateTaxcode();
            this.cdr.detectChanges();
          }
        } else {
          this.calculateTaxcode();
          this.cdr.detectChanges();
        }
      }
      this.setControlValidator('taxcode', [Validators.required]);
      !this.isAdult ? this.setControlValidator('legalProtectionMinor', [Validators.requiredTrue]) : null;
    });
  }

  private allTaxCodeFieldsAreFilled(): boolean {
    const valuesForm = this.createProfileForm?.value;
    return (
      !!valuesForm.name &&
      !!valuesForm.surname &&
      !!valuesForm.birthdate &&
      !!valuesForm.taxcodeSection?.gender &&
      !!valuesForm.taxcodeSection?.birthDistrict &&
      !!valuesForm.taxcodeSection?.birthCity
    );
  }

  private checkOldValues(previous: any, current: any): boolean {
    return (
      previous.name !== current.name ||
      previous.surname !== current.surname ||
      previous.birthdate !== current.birthdate ||
      previous.taxcodeSection?.birthCity !== current.taxcodeSection?.birthCity ||
      previous.taxcodeSection?.birthDistrict !== current.taxcodeSection?.birthDistrict ||
      previous.taxcodeSection?.gender !== current.taxcodeSection?.gender
    );
  }

  private getFormValidation() {
    return (
      this.createProfileForm?.get('taxcodeSection')?.valid &&
      this.createProfileForm?.get('name')?.valid &&
      this.createProfileForm?.get('surname')?.valid &&
      this.createProfileForm?.get('birthdate')?.valid
    );
  }

  clearControlValidators(controlName: string) {
    const taxcodeControl = this.createProfileForm!.get(controlName);
    taxcodeControl?.clearValidators();
    taxcodeControl?.updateValueAndValidity({ emitEvent: false });
  }

  setControlValidator(controlName: string, validators: Array<ValidatorFn>) {
    const taxcodeControl = this.createProfileForm!.get(controlName);
    taxcodeControl?.setValidators(validators);
    taxcodeControl?.updateValueAndValidity({ emitEvent: false });
  }

  private calculateTaxcode(): void {
    const valuesForm = this.createProfileForm?.value;
    const taxcodeSection = valuesForm.taxcodeSection;
    if (
      !valuesForm.name ||
      !valuesForm.surname ||
      !valuesForm.birthdate ||
      !taxcodeSection?.gender ||
      !taxcodeSection?.birthCity ||
      !taxcodeSection?.birthDistrict
    ) {
      this.toastr.error(
        this.translate.instant('toastr.error.fillAllTaxCodeFields')
      );
      return;
    }
    try {
      const city = this.cities.find(
        (item) => item.value === taxcodeSection.birthCity
      );
      const birthDate = this.parseBirthDate(valuesForm.birthdate);
      const generatedCF = this.fiscalCodeService.generateFiscalCode({
        firstName: valuesForm.name,
        lastName: valuesForm.surname,
        gender: taxcodeSection.gender,
        birthDate,
        birthCity: city?.label ?? taxcodeSection.birthCity,
        birthProvince: taxcodeSection.birthDistrict,
      });
      this.createProfileForm!.get('taxcode')?.patchValue(
        generatedCF,
        { emitEvent: false }
      );
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Error calculating fiscal code', err);
      this.toastr.error(
        this.translate.instant('toastr.error.invalidFiscalCode')
      );
    }
  }

  
  protected onSend(): void {
    const user = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
    if (user.functions.includes('MCE') && this.isAdult) {
      this.toast.error(this.translate.instant('toastr.error.createProfileError'));
      this.modalRef.close();
      return;
    }
    if (this.isAdult) {
      const mainIndividualId = user.mainIndividualId;
      const userData = {
        principalIndividualId: mainIndividualId,
        name: this.createProfileForm?.get('name')?.value,
        surname: this.createProfileForm?.get('surname')?.value,
        taxId: this.createProfileForm?.get('taxcode')?.value,
        userName: this.createProfileForm?.get('email')?.value,
        email: this.createProfileForm?.get('email')?.value,
        sendDataExternalUser: false,
        phone: this.createProfileForm?.get('phone')?.value,
      };
      this.http
        .createAdultMedicalProfile(userData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (_) => {
            this.toast.success('Profilo creato correttamente!');
            this.dataModal.actionButton();
          },
        });
    } else {
      const userData = {
        firstName: this.createProfileForm?.get('name')?.value,
        lastName: this.createProfileForm?.get('surname')?.value,
        taxId: this.createProfileForm?.get('taxcode')?.value,
      };

      this.http
        .createMedicalProfile(userData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (_) => {
            this.toast.success('Profilo creato correttamente!');
            this.dataModal.actionButton();
          },
        });
    }
  }

  protected onExit(): void {
    this.modalRef.close();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
